import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseUkHoldingPeriodsOptions = {
  taxYear: number;
  enabled?: boolean;
};

export type UkHoldingPeriodsResponse = {
  taxYear: number;
  taxSetting: any;
  holdingPeriods: any[];
};

export const useUkHoldingPeriods = ({
  taxYear,
  enabled = true,
}: UseUkHoldingPeriodsOptions): UseQueryResult<UkHoldingPeriodsResponse | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchHoldingPeriods = async (): Promise<UkHoldingPeriodsResponse | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get(
        "/export/tax-report/uk/holding-periods",
        {
          params: { year: taxYear },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      if (status === 400 && code === "unsupported_jurisdiction") {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["uk_holding_periods", taxYear],
    queryFn: fetchHoldingPeriods,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
